import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { toast } from 'sonner';
import {
  cleanupBexioReconOrphans,
  getBexioReconciliation,
  getDealLinkOptions,
  getMaintenanceItems,
  linkBexioMaintenanceInvoice,
  linkBexioReconInvoice,
  syncBexioReconciliationPaid,
  getForecastAnnualTarget,
  getForecastHistory,
  getForecastKpiExplain,
  getForecastMonthlyPerformance,
  getForecastSummary,
  getForecastTimeline,
  getReceivablesByClient,
  getReceivablesByDeal,
  getReceivablesByMonth,
  getReceivablesByProject,
  getReceivablesSummary,
} from '../lib/api';
import { formatNumber } from '../lib/utils';
import { DEAL_STAGE_VALUES_ORDERED, getStageLabel } from '../constants/dealStages';
import { BexioReconDealPicker } from './BexioReconDealPicker';
import { BexioReconMaintenancePicker } from './BexioReconMaintenancePicker';
import { ForecastAnnualTargetCard } from './ForecastAnnualTargetCard';
import { ForecastHistoryChart } from './ForecastHistoryChart';
import { ForecastKpiExplainSheet } from './ForecastKpiExplainSheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

function buildYearOptions() {
  const current = new Date().getFullYear();
  const years = [current - 1, current, current + 1, current + 2];
  return [
    { value: 'all', label: 'All years' },
    ...years.map((y) => ({ value: String(y), label: String(y) })),
  ];
}

const YEAR_OPTIONS = buildYearOptions();

function defaultYearFilter() {
  const y = String(new Date().getFullYear());
  return YEAR_OPTIONS.some((o) => o.value === y) ? y : 'all';
}

function defaultFromMonth() {
  const y = new Date().getFullYear();
  return YEAR_OPTIONS.some((o) => o.value === String(y)) ? '' : `${y}-01`;
}

function defaultToMonth() {
  const y = new Date().getFullYear();
  return YEAR_OPTIONS.some((o) => o.value === String(y)) ? '' : `${y}-12`;
}

function formatMonthLabel(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return monthKey || '';
  const [y, m] = monthKey.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function currentMonthKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

// Projected Sales = stage-weighted revenue still to close (Demand + Quote Issued).
// Hidden for past months — once the month ends, only Opening Forecast and Actual
// Revenue remain meaningful.
function isPastMonth(monthKey) {
  if (!monthKey) return false;
  return monthKey < currentMonthKey();
}

function addMonths(yyyymm, delta) {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function enumerateMonths(fromKey, toKey) {
  if (!fromKey || !toKey || fromKey > toKey) return null;
  const out = [];
  let cursor = fromKey;
  // Hard cap at 120 months to avoid accidental runaway loops.
  for (let i = 0; i < 120 && cursor <= toKey; i += 1) {
    out.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return out;
}

// Synthetic receivables-by-month row: "Outside selected period" is not a
// real YYYY-MM bucket — use this key in ``expandedMonths`` so expand/collapse
// state never collides with a calendar month.
const RECEIVABLES_OUTSIDE_EXPAND_KEY = '__receivables_outside__';

function deltaPct(opening, delta) {
  if (!opening) return '0.0%';
  return `${((delta / opening) * 100).toFixed(1)}%`;
}

function negativePurpleClass(value) {
  // `!` forces override of `.pl-cell-num`'s baked-in text-gray-900 (declared
  // outside @layer components in index.css, so it sits after utilities in the
  // compiled CSS and would otherwise win against plain text-purple-700).
  return Number(value) < 0 ? '!text-purple-700' : '';
}

function deltaColorClass(value) {
  if (value === null || value === undefined) return 'text-gray-400';
  return Number(value) >= 0 ? '!text-green-700' : '!text-purple-700';
}

// Per-invoice chip for the monthly receivables table.
// - Overdue → red pill with `Nd overdue` badge.
// - Due in the future → neutral black-on-grey pill with `Nd left`.
// - Uninvoiced (backend ``status="uninvoiced"`` deal residual) → dashed amber
//   pill to flag the missing invoice.
// When ``onOpenDeal`` is set and the row has ``deal_id``, the chip is a button
// that opens that deal (clicks do not bubble to month / library drill rows).
function MonthlyInvoiceChip({ invoice, onOpenDeal }) {
  // Synthetic uninvoiced rows use ``status="uninvoiced"`` and empty
  // ``document_nr`` — dashed amber, no day counter (no invoice clock).
  const isUninvoiced = invoice.status === 'uninvoiced';
  const isOverdue = !isUninvoiced && (invoice.days_overdue || 0) > 0;
  const label = invoice.document_nr || '(uninvoiced)';
  const titleParts = [];
  if (invoice.project_name) titleParts.push(invoice.project_name);
  if (invoice.client_name) titleParts.push(invoice.client_name);
  if (invoice.due_date) titleParts.push(`Due ${invoice.due_date}`);
  if (invoice.status) titleParts.push(`Status: ${invoice.status}`);
  const containerClass = isUninvoiced
    ? 'border-amber-300 bg-amber-50 text-amber-800 border-dashed'
    : isOverdue
      ? 'border-red-200 bg-red-50 text-red-800'
      : 'border-gray-200 bg-gray-50 text-gray-800';
  let daysText = '';
  if (!isUninvoiced) {
    if (isOverdue) {
      daysText = `${invoice.days_overdue}d overdue`;
    } else if ((invoice.days_until_due || 0) > 0) {
      daysText = `${invoice.days_until_due}d left`;
    } else {
      daysText = 'due today';
    }
  }
  const daysClass = isOverdue ? 'text-red-700 font-semibold' : 'text-gray-700';
  const chipClass = `inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1 py-0 text-[10px] font-mono leading-4 ${containerClass}`;
  const canOpenDeal = typeof onOpenDeal === 'function' && Boolean(invoice.deal_id);
  const titleBase = titleParts.join(' · ');
  const title = canOpenDeal
    ? (titleBase ? `${titleBase} · Click to open deal` : 'Click to open deal')
    : titleBase || undefined;
  const inner = (
    <>
      <span className="tabular-nums">{label}</span>
      <span className="text-[10px] font-normal text-gray-500">{formatNumber(invoice.outstanding_chf)} CHF</span>
      {daysText ? (
        <span className={`ml-0.5 text-[10px] tabular-nums ${daysClass}`}>{daysText}</span>
      ) : null}
    </>
  );
  if (canOpenDeal) {
    return (
      <button
        type="button"
        className={`${chipClass} cursor-pointer text-left hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1`}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          onOpenDeal(invoice.deal_id);
        }}
      >
        {inner}
      </button>
    );
  }
  return (
    <span className={chipClass} title={titleBase || undefined}>
      {inner}
    </span>
  );
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
}

// Invoice chip: renders a Bexio document number with an inline overdue badge
// so the user sees "AR-0042   28d overdue" next to each issued invoice.
function InvoiceChip({ invoice, tone = 'issued' }) {
  const base = tone === 'paid'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : (invoice.days_overdue > 0
      ? 'border-red-200 bg-red-50 text-red-800'
      : 'border-sky-200 bg-sky-50 text-sky-800');
  // Bexio doc-nr is the only trustworthy label — never fabricate from tranche_id.
  const label = invoice.document_nr || 'N/A';
  // Issued chips show open (outstanding) amount so drill totals match KPIs;
  // paid chips keep face value.
  const displayAmount = tone === 'issued' && invoice.outstanding_chf != null
    ? invoice.outstanding_chf
    : invoice.amount_chf;
  const titleParts = [];
  if (invoice.external_title) titleParts.push(invoice.external_title);
  if (invoice.issue_date) titleParts.push(`Issued ${invoice.issue_date}`);
  if (invoice.due_date) titleParts.push(`Due ${invoice.due_date}`);
  if (invoice.tranche_type) titleParts.push(`Tranche: ${invoice.tranche_type}`);
  if (tone === 'issued' && invoice.outstanding_chf != null && invoice.amount_chf != null
      && Math.abs(Number(invoice.outstanding_chf) - Number(invoice.amount_chf)) > 0.01) {
    titleParts.push(`Face ${formatNumber(invoice.amount_chf)} CHF`);
  }
  const title = titleParts.join(' · ');
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1 py-0 text-[10px] font-mono leading-4 ${base}`}
      title={title}
    >
      <span className="tabular-nums">{label}</span>
      <span className="text-[10px] font-normal text-gray-500">{formatNumber(displayAmount)} CHF</span>
      {tone === 'issued' && invoice.days_overdue > 0 ? (
        <span className="ml-0.5 rounded-sm bg-red-600 px-1 py-0 text-[9px] font-bold uppercase text-white">
          {invoice.days_overdue}d
        </span>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Bexio reconciliation — badge + drawer
// ---------------------------------------------------------------------------
// The badge answers: "for the invoices we track in common with Bexio, how
// far does our system drift from Bexio's ground truth?". Bexio is the
// benchmark, so delta = bexio − system. Bexio AR outside our deal pipeline
// is surfaced separately as ``bexio_unlinked_outstanding_chf`` so it cannot
// create permanent false drift. Four visual states:
//   * gray   — not configured (no token) OR loading.
//   * green  — delta in_sync AND zero drift / orphan / phantom / unmapped.
//   * amber  — any mismatch in the linked scope OR truncated/stale fetch.
//   * red    — hard error or catastrophic delta (> 1000 CHF).

function bexioDeltaLabel(recon) {
  if (!recon) return '—';
  const d = Number(recon.delta_chf || 0);
  const abs = Math.abs(d).toFixed(2);
  // Bexio is the benchmark. delta_chf = booking-FX-aligned bexio − system:
  //   positive → Bexio has MORE than us → we're missing a link.
  //   negative → Bexio has LESS than us → we carry a phantom.
  // FX mark-to-market gaps live in fx_translation_variance_chf, not here.
  if (d > 0) return `System missing ${formatNumber(Number(abs))} CHF vs Bexio`;
  if (d < 0) return `System has ${formatNumber(Number(abs))} CHF phantom vs Bexio`;
  return 'In sync with Bexio';
}

function bexioFxVarianceAbs(recon) {
  const v = Number(recon?.fx_translation_variance_chf || 0);
  return Math.abs(v) > 0.5 ? Math.abs(v) : 0;
}

function bexioBadgeTone(recon, error) {
  if (error) return 'red';
  if (!recon) return 'gray';
  if (recon.state === 'not_configured') return 'gray';
  if (recon.state === 'error') return 'red';
  const unlinkedCount = recon.unlinked_count ?? (recon.unlinked_in_bexio?.length || 0);
  const missingCount = recon.missing_count ?? (recon.missing_in_bexio?.length || 0);
  const unmappedCount = recon.unmapped_count ?? (recon.unmapped_currencies?.length || 0);
  const orphanCount = recon.orphan_count ?? (recon.orphan_tranches?.length || 0);
  const hasIssues =
    (recon.drift_count || 0) > 0
    || unlinkedCount > 0
    || missingCount > 0
    || unmappedCount > 0
    || orphanCount > 0
    || (recon.delta_status && recon.delta_status !== 'in_sync');
  // FX translation variance alone is informational — never red.
  if (Math.abs(recon.delta_chf || 0) > 1000) return 'red';
  if (orphanCount > 0) return 'red';
  if (hasIssues || recon.stale) return 'amber';
  return 'green';
}

function BexioReconBadge({ recon, loading, error, onClick }) {
  const tone = bexioBadgeTone(recon, error);
  const toneClass = {
    gray: 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100',
    red: 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100',
  }[tone];
  const dotClass = {
    gray: 'bg-gray-400',
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  }[tone];

  let label = 'Bexio check';
  let sub = '';
  if (loading) {
    label = 'Checking Bexio…';
  } else if (error) {
    label = 'Bexio unreachable';
  } else if (!recon) {
    label = 'Bexio check';
  } else if (recon.state === 'not_configured') {
    label = 'Bexio not configured';
  } else if (recon.state === 'error') {
    label = 'Bexio error';
    sub = recon.message || '';
  } else {
    label = bexioDeltaLabel(recon);
    const driftCount = recon.drift_count || 0;
    const unlinkedCount = recon.unlinked_count ?? (recon.unlinked_in_bexio?.length || 0);
    const missingCount = recon.missing_count ?? (recon.missing_in_bexio?.length || 0);
    const unmappedCount = recon.unmapped_count ?? (recon.unmapped_currencies?.length || 0);
    const orphanCount = recon.orphan_count ?? (recon.orphan_tranches?.length || 0);
    const fxVarAbs = bexioFxVarianceAbs(recon);
    const badges = [];
    if (driftCount > 0) badges.push(`${driftCount} drift`);
    if (unlinkedCount > 0) badges.push(`${unlinkedCount} orphan`);
    if (missingCount > 0) badges.push(`${missingCount} phantom`);
    if (orphanCount > 0) badges.push(`${orphanCount} stale tranche${orphanCount === 1 ? '' : 's'}`);
    if (unmappedCount > 0) badges.push(`${unmappedCount} unmapped FX`);
    if (fxVarAbs > 0) badges.push(`${formatNumber(fxVarAbs)} CHF FX variance`);
    if (recon.stale) badges.push('stale');
    sub = badges.join(' · ');
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border px-3 py-1 text-xs font-medium shadow-sm transition-colors ${toneClass}`}
      data-testid="bexio-recon-badge"
      title="Open the Bexio reconciliation drawer to see which invoices differ between our system and Bexio."
    >
      <span className="flex items-center gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
        <span className="tabular-nums">{label}</span>
      </span>
      {sub ? <span className="text-[11px] opacity-80">{sub}</span> : null}
    </button>
  );
}

function BexioReconSection({ title, tone, count, previewLen, children }) {
  if (!count) return null;
  const toneBorder = {
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
    blue: 'border-blue-200 bg-blue-50',
    gray: 'border-gray-200 bg-gray-50',
  }[tone] || 'border-gray-200 bg-gray-50';
  // When the preview list is shorter than the true count, the server has
  // truncated at the list cap. Surface that explicitly so the user never
  // mistakes "we see 200" for "there are 200".
  const truncated = typeof previewLen === 'number' && previewLen > 0 && previewLen < count;
  return (
    <section className={`rounded-lg border p-3 ${toneBorder}`}>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-700">{title}</h4>
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold tabular-nums text-gray-700 shadow-sm">
          {count}
        </span>
      </div>
      {children}
      {truncated ? (
        <p className="mt-2 text-[11px] italic text-gray-600">
          Showing first {previewLen} of {count}; remaining rows truncated for payload size.
        </p>
      ) : null}
    </section>
  );
}

function unlinkedPrefersMaintenance(row) {
  return Boolean(
    row?.likely_maintenance_match
    || row?.maintenance_title_hint
    || (row?.maintenance_candidates && row.maintenance_candidates.length > 0)
  );
}

function BexioReconDrawer({ open, onClose, recon, loading, error, onRefresh, onCleanupOrphans }) {
  const [dealOptions, setDealOptions] = useState([]);
  const [dealOptionsError, setDealOptionsError] = useState('');
  const [maintOptions, setMaintOptions] = useState([]);
  const [maintOptionsError, setMaintOptionsError] = useState('');
  const [selectedDealByInvoice, setSelectedDealByInvoice] = useState({});
  const [selectedMaintByInvoice, setSelectedMaintByInvoice] = useState({});
  const [showDealLinkByInvoice, setShowDealLinkByInvoice] = useState({});
  const [linkingInvoiceId, setLinkingInvoiceId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setDealOptionsError('');
    setMaintOptionsError('');
    (async () => {
      try {
        const res = await getDealLinkOptions();
        if (!cancelled) setDealOptions(res.data || []);
      } catch {
        if (!cancelled) {
          setDealOptions([]);
          setDealOptionsError('Could not load deals for linking.');
        }
      }
    })();
    (async () => {
      try {
        const res = await getMaintenanceItems({ state: 'active' });
        if (!cancelled) setMaintOptions(res.data || []);
      } catch {
        if (!cancelled) {
          setMaintOptions([]);
          setMaintOptionsError('Could not load maintenance sites for linking.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Prefill maintenance picker from recon candidates when the drawer data changes.
  useEffect(() => {
    if (!open || !recon?.unlinked_in_bexio?.length) return;
    setSelectedMaintByInvoice((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const row of recon.unlinked_in_bexio) {
        const invoiceId = Number(row.invoice_id);
        if (!invoiceId || next[invoiceId]) continue;
        const preferred = (row.maintenance_candidates || []).find((c) => c.item_id);
        if (preferred?.item_id) {
          next[invoiceId] = preferred.item_id;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, recon]);

  const linkUnlinkedInvoice = async (row) => {
    const invoiceId = Number(row?.invoice_id);
    const dealId = String(selectedDealByInvoice[invoiceId] || '').trim();
    if (!dealId) {
      toast.error('Select a deal first.');
      return;
    }
    setLinkingInvoiceId(invoiceId);
    try {
      const res = await linkBexioReconInvoice(dealId, invoiceId);
      const doc = res.data?.document_nr || row.document_nr || `#${invoiceId}`;
      toast.success(`Linked ${doc} to the selected deal`);
      setSelectedDealByInvoice((prev) => {
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      });
      await onRefresh({ refresh: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not link invoice to deal');
    } finally {
      setLinkingInvoiceId(null);
    }
  };

  const linkUnlinkedToMaintenance = async (row) => {
    const invoiceId = Number(row?.invoice_id);
    const itemId = String(selectedMaintByInvoice[invoiceId] || '').trim();
    if (!itemId) {
      toast.error('Select a maintenance site first.');
      return;
    }
    setLinkingInvoiceId(invoiceId);
    try {
      await linkBexioMaintenanceInvoice(itemId, invoiceId);
      const doc = row.document_nr || `#${invoiceId}`;
      const site = maintOptions.find((m) => m.id === itemId);
      toast.success(`Linked ${doc} to maintenance ${site?.domain_name || itemId}`);
      setSelectedMaintByInvoice((prev) => {
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      });
      await onRefresh({ refresh: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not link invoice to maintenance');
    } finally {
      setLinkingInvoiceId(null);
    }
  };

  if (!open) return null;
  const fetchedAt = recon?.fetched_at ? new Date(recon.fetched_at) : null;
  const fetchedLabel = fetchedAt ? fetchedAt.toLocaleString() : '—';
  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Bexio reconciliation">
      <button
        type="button"
        aria-label="Close Bexio reconciliation drawer"
        className="flex-1 bg-black/30"
        onClick={onClose}
      />
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden bg-white shadow-xl"
        data-testid="bexio-recon-drawer"
      >
        <header className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Bexio reconciliation</h3>
            <p className="mt-0.5 text-[11px] text-gray-500">
              Cross-check of our open AR against Bexio's open-invoice ledger. Bexio is the source of truth for "issued / paid".
              Amounts are compared on a <strong>net (excl. VAT)</strong> basis so the CHF VAT markup doesn't surface as noise —
              any remaining drift reflects a real discrepancy (e.g. USD invoices stored without an FX rate).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => onRefresh({ refresh: true })}
              disabled={loading}
              data-testid="bexio-recon-refresh"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          ) : null}
          {!recon && !error ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
              Loading…
            </div>
          ) : null}
          {recon?.state === 'not_configured' ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
              {recon.message || 'Bexio access token is not configured; reconciliation is disabled.'}
            </div>
          ) : null}
          {recon?.state === 'ok' ? (
            <div className="space-y-3">
              {/* Scope panel — Bexio is the BENCHMARK. Lead with Bexio's
                  number on the left; our system sits alongside; the delta
                  is booking-FX aligned (Δ = bexio@booking − system). */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Bexio linked AR (benchmark, CHF)</p>
                  <p className="text-sm font-semibold tabular-nums text-gray-900">{formatNumber(recon.bexio_linked_outstanding_chf)}</p>
                  <p className="text-[11px] text-gray-500">Open in Bexio at today&apos;s FX (display).</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">System linked AR (CHF)</p>
                  <p className="text-sm font-semibold tabular-nums text-gray-900">{formatNumber(recon.linked_system_outstanding_chf)}</p>
                  <p className="text-[11px] text-gray-500">Our billed tranches at booking FX.</p>
                </div>
                <div
                  className={`rounded-md border px-3 py-2 ${
                    recon.delta_status === 'in_sync'
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Δ vs Bexio (linked scope)</p>
                  <p className="text-sm font-semibold tabular-nums text-gray-900">
                    {recon.delta_chf > 0 ? '+' : ''}{formatNumber(recon.delta_chf)} CHF
                  </p>
                  <p className="text-[11px] text-gray-600">
                    {recon.delta_status === 'in_sync'
                      ? 'Within ±0.50 CHF on booking-FX basis (FX variance excluded).'
                      : recon.delta_status === 'bexio_excess'
                        ? 'Bexio shows MORE than us → likely a missing link on our side.'
                        : 'Bexio shows LESS than us → likely a phantom invoice on our side.'}
                  </p>
                </div>
              </div>
              {bexioFxVarianceAbs(recon) > 0 ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-5 text-slate-700">
                  <span className="font-semibold text-slate-800">FX translation variance:</span>{' '}
                  <span className="tabular-nums">
                    {Number(recon.fx_translation_variance_chf) > 0 ? '+' : ''}
                    {formatNumber(recon.fx_translation_variance_chf)} CHF
                  </span>
                  <span className="ml-1 text-slate-500">
                    (Bexio @ today&apos;s FX minus Bexio @ booking rates — not a phantom)
                  </span>
                </div>
              ) : null}

              {/* Cross-reference strip — total AR, uninvoiced residual, and
                  Bexio-only open invoices (company AR outside our deal
                  pipeline, NOT folded into the delta above). */}
              <div className="rounded-md border border-gray-100 bg-white px-3 py-2 text-[11px] leading-5 text-gray-600">
                <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-3">
                  <div>
                    <span className="font-semibold text-gray-700">Dashboard Total AR:</span>{' '}
                    <span className="tabular-nums">{formatNumber(recon.total_receivables_chf)} CHF</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Uninvoiced residual:</span>{' '}
                    <span className="tabular-nums">{formatNumber(recon.uninvoiced_residual_chf)} CHF</span>
                    <span className="ml-1 text-gray-500">(not yet posted to Bexio)</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Bexio-only open AR:</span>{' '}
                    <span className="tabular-nums">{formatNumber(recon.bexio_unlinked_outstanding_chf)} CHF</span>
                    <span className="ml-1 text-gray-500">(outside deal pipeline)</span>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-gray-500">
                {recon.matched_count} linked invoice(s){recon.drift_count ? `, ${recon.drift_count} with drift` : ''}. Fetched {fetchedLabel}.
                {recon.cached ? ' (cached)' : ''}{recon.stale ? ' (stale)' : ''}
                {recon.message ? ` · ${recon.message}` : ''}
              </p>

              <BexioReconSection
                title="Unlinked in Bexio (missing in system)"
                tone="amber"
                count={recon.unlinked_count ?? (recon.unlinked_in_bexio?.length || 0)}
                previewLen={recon.unlinked_in_bexio?.length || 0}
              >
                <ul className="divide-y divide-amber-100">
                  {recon.unlinked_in_bexio.map((r) => {
                    const prefersMaint = unlinkedPrefersMaintenance(r);
                    const preferredMaintIds = (r.maintenance_candidates || [])
                      .map((c) => c.item_id)
                      .filter(Boolean);
                    const showDealLink = !prefersMaint || showDealLinkByInvoice[r.invoice_id];
                    const hasCatalogCandidates = preferredMaintIds.length > 0
                      || (r.maintenance_candidates || []).length > 0;
                    return (
                      <li key={`ub-${r.invoice_id}`} className="py-2 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-col">
                            <span className="font-mono font-semibold text-gray-900">{r.document_nr || `#${r.invoice_id}`}</span>
                            {r.title ? (
                              <span className="truncate text-[11px] text-gray-700" title={r.title}>{r.title}</span>
                            ) : null}
                            <span className="text-[11px] text-gray-600">
                              {r.currency_code || 'CHF'} · {r.status_label || 'open'} · from {r.is_valid_from || '—'}
                            </span>
                            {r.likely_maintenance_match && r.maintenance_hint_domains ? (
                              <span className="mt-0.5 text-[10px] text-amber-800">
                                Maintenance match: {r.maintenance_hint_domains}
                              </span>
                            ) : null}
                            {r.maintenance_title_hint && !hasCatalogCandidates ? (
                              <span className="mt-0.5 text-[10px] text-amber-800">
                                Looks like a hosting/domain renewal, but no maintenance site matches yet — create the site in Maintenance, then link here.
                              </span>
                            ) : null}
                          </div>
                          <span className="shrink-0 tabular-nums font-semibold text-gray-900">
                            {r.remaining_chf != null ? `${formatNumber(r.remaining_chf)} CHF` : `${formatNumber(r.remaining_raw)} ${r.currency_code || ''}`}
                          </span>
                        </div>

                        {prefersMaint ? (
                          <div className="mt-1.5 flex items-center gap-1.5">
                            <BexioReconMaintenancePicker
                              items={maintOptions}
                              value={selectedMaintByInvoice[r.invoice_id] || ''}
                              preferredIds={preferredMaintIds}
                              onChange={(itemId) => {
                                setSelectedMaintByInvoice((prev) => ({ ...prev, [r.invoice_id]: itemId }));
                              }}
                              disabled={loading || linkingInvoiceId != null || maintOptions.length === 0}
                              hintText={maintOptionsError || 'Search by domain or customer'}
                            />
                            <button
                              type="button"
                              className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={
                                loading
                                || linkingInvoiceId != null
                                || !selectedMaintByInvoice[r.invoice_id]
                              }
                              data-testid={`bexio-recon-link-maint-${r.invoice_id}`}
                              onClick={() => linkUnlinkedToMaintenance(r)}
                            >
                              {linkingInvoiceId === r.invoice_id ? 'Linking…' : 'Link maintenance'}
                            </button>
                          </div>
                        ) : null}

                        {showDealLink ? (
                          <div className={`flex items-center gap-1.5 ${prefersMaint ? 'mt-1' : 'mt-1.5'}`}>
                            <BexioReconDealPicker
                              deals={dealOptions}
                              value={selectedDealByInvoice[r.invoice_id] || ''}
                              onChange={(dealId) => {
                                setSelectedDealByInvoice((prev) => ({ ...prev, [r.invoice_id]: dealId }));
                              }}
                              disabled={loading || linkingInvoiceId != null}
                              hintText={dealOptionsError || 'Search by project or client'}
                            />
                            <button
                              type="button"
                              className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={
                                loading
                                || linkingInvoiceId != null
                                || !selectedDealByInvoice[r.invoice_id]
                              }
                              data-testid={`bexio-recon-link-${r.invoice_id}`}
                              onClick={() => linkUnlinkedInvoice(r)}
                            >
                              {linkingInvoiceId === r.invoice_id ? 'Linking…' : 'Link deal'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="mt-1 text-[10px] font-medium text-amber-800/80 underline-offset-2 hover:underline"
                            onClick={() => {
                              setShowDealLinkByInvoice((prev) => ({ ...prev, [r.invoice_id]: true }));
                            }}
                          >
                            Not maintenance? Link to a deal instead
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {(dealOptionsError || maintOptionsError) ? (
                  <p className="mt-1.5 text-[11px] text-amber-800">{dealOptionsError || maintOptionsError}</p>
                ) : (
                  <p className="mt-1.5 text-[11px] text-amber-800/80">
                    Hosting/domain renewals: link to a maintenance site. Deal AR: link to the related deal. Both stay in this drawer — no Maintenance tab detour required when the site already exists.
                  </p>
                )}
              </BexioReconSection>

              <BexioReconSection
                title="Missing in Bexio (phantom in system)"
                tone="red"
                count={recon.missing_count ?? (recon.missing_in_bexio?.length || 0)}
                previewLen={recon.missing_in_bexio?.length || 0}
              >
                <ul className="divide-y divide-red-100">
                  {recon.missing_in_bexio.map((r) => (
                    <li key={`mb-${r.tranche_id}`} className="flex items-center justify-between py-1.5 text-xs">
                      <div className="flex flex-col">
                        <span className="font-mono font-semibold text-gray-900">{r.document_nr || `(inv ${r.external_invoice_id})`}</span>
                        <span className="text-[11px] text-gray-600">
                          {r.client_name || '—'} · {r.project_name || '—'} · {r.status}
                        </span>
                      </div>
                      <span className="tabular-nums font-semibold text-gray-900">{formatNumber(r.outstanding_chf)} CHF</span>
                    </li>
                  ))}
                </ul>
              </BexioReconSection>

              <BexioReconSection
                title="Orphan tranches (cleanup needed)"
                tone="red"
                count={recon.orphan_count ?? (recon.orphan_tranches?.length || 0)}
                previewLen={recon.orphan_tranches?.length || 0}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <p className="text-[11px] text-red-800">
                    Tranches that still claim a Bexio invoice they shouldn't.
                    "Deleted deal" rows are removed; "stale link" rows (renewed
                    cycles or reset links) just have their Bexio reference cleared
                    so the duplicate-link guard stops blocking new links — payment
                    history is kept.
                  </p>
                  {onCleanupOrphans ? (
                    <button
                      type="button"
                      data-testid="bexio-recon-cleanup-orphans"
                      className="shrink-0 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-semibold text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => {
                        const rows = recon.orphan_tranches || [];
                        const deletedDeals = rows.filter((r) => r.orphan_kind !== 'stale_maintenance_link').length;
                        const staleLinks = rows.filter((r) => r.orphan_kind === 'stale_maintenance_link').length;
                        const parts = [];
                        if (deletedDeals) parts.push(`delete ${deletedDeals} deleted-deal tranche${deletedDeals === 1 ? '' : 's'} (and their payments)`);
                        if (staleLinks) parts.push(`clear the Bexio link on ${staleLinks} stale maintenance tranche${staleLinks === 1 ? '' : 's'} (payments preserved)`);
                        const summary = parts.join(' and ') || 'clean up orphan tranches';
                        const ok = window.confirm(`This will ${summary}. Proceed?`);
                        if (ok) onCleanupOrphans();
                      }}
                      disabled={loading}
                    >
                      Clean up
                    </button>
                  ) : null}
                </div>
                <ul className="divide-y divide-red-100">
                  {recon.orphan_tranches?.map((r) => {
                    const isStale = r.orphan_kind === 'stale_maintenance_link';
                    return (
                      <li key={`ot-${r.tranche_id}`} className="flex items-start justify-between gap-3 py-1.5 text-xs">
                        <div className="flex min-w-0 flex-col">
                          <span className="font-mono font-semibold text-gray-900">{r.document_nr || `(inv ${r.external_invoice_id})`}</span>
                          <span className="text-[11px] text-gray-600">
                            {isStale ? 'stale maintenance link' : 'missing deal'} · {r.status}
                          </span>
                          <span className="font-mono text-[10px] text-gray-500">tranche: {r.tranche_id}</span>
                          {isStale && r.source_id ? (
                            <span className="font-mono text-[10px] text-gray-500">maintenance: {r.source_id}</span>
                          ) : (
                            <span className="font-mono text-[10px] text-gray-500">deal: {r.deal_id}</span>
                          )}
                        </div>
                        <span className="tabular-nums font-semibold text-gray-900">{formatNumber(r.outstanding_chf)} CHF</span>
                      </li>
                    );
                  })}
                </ul>
              </BexioReconSection>

              <BexioReconSection
                title="Linked with drift"
                tone="amber"
                count={recon.drift_count ?? (recon.linked_with_drift?.length || 0)}
                previewLen={recon.linked_with_drift?.length || 0}
              >
                <ul className="divide-y divide-amber-100">
                  {recon.linked_with_drift.map((r) => {
                    // Native comparison strip — Bexio first (benchmark).
                    const nativeLabel = r.drift_basis === 'native' && r.source_currency
                      ? `${r.source_currency} Bexio ${formatNumber(r.bexio_remaining_native)} vs system ${formatNumber(r.system_outstanding_native)} (native Δ ${r.native_delta > 0 ? '+' : ''}${formatNumber(r.native_delta)})`
                      : null;
                    return (
                      <li key={`ld-${r.tranche_id}-${r.invoice_id}`} className="flex items-start justify-between gap-3 py-1.5 text-xs">
                        <div className="flex min-w-0 flex-col">
                          <span className="font-mono font-semibold text-gray-900">{r.document_nr}</span>
                          <span className="text-[11px] text-gray-600">
                            {r.client_name || '—'} · {r.project_name || '—'}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            Bexio {formatNumber(r.bexio_remaining_chf)} CHF vs system {formatNumber(r.system_outstanding_chf)} CHF
                          </span>
                          {nativeLabel ? (
                            <span className="text-[11px] text-gray-500">{nativeLabel}</span>
                          ) : null}
                        </div>
                        <div className="flex flex-col items-end">
                          <span
                            className={`tabular-nums font-semibold ${
                              r.delta_chf > 0 ? 'text-amber-800' : 'text-red-700'
                            }`}
                          >
                            {r.delta_chf > 0 ? '+' : ''}{formatNumber(r.delta_chf)} CHF
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-gray-400">
                            {r.drift_basis === 'native' ? 'native-currency Δ' : 'CHF Δ'}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </BexioReconSection>

              {recon.linked_in_bexio?.length ? (
                <details className="rounded-lg border border-gray-200 bg-white p-3">
                  <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-700">
                    <span>All linked invoices (diagnostic)</span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-gray-700">
                      {recon.matched_count ?? recon.linked_in_bexio.length}
                    </span>
                  </summary>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Every Bexio open invoice we joined to a system tranche. Use this to
                    find which tranche claims a Bexio invoice when the deal/maintenance
                    UI shows no link (typically a stale tranche from a renewed
                    maintenance cycle or a mis-linked deal).
                  </p>
                  <ul className="mt-2 divide-y divide-gray-100">
                    {recon.linked_in_bexio.map((r) => (
                      <li key={`la-${r.tranche_id}-${r.invoice_id}`} className="flex items-start justify-between gap-3 py-1.5 text-xs">
                        <div className="flex min-w-0 flex-col">
                          <span className="font-mono font-semibold text-gray-900">{r.document_nr || `#${r.invoice_id}`}</span>
                          <span className="text-[11px] text-gray-600">
                            {r.client_name || '—'} · {r.project_name || '—'} · {r.tranche_status || '—'}
                          </span>
                          <span className="font-mono text-[10px] text-gray-500">tranche: {r.tranche_id}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="tabular-nums font-semibold text-gray-900">
                            {formatNumber(r.bexio_remaining_chf)} CHF
                          </span>
                          <span className="text-[10px] text-gray-500">
                            sys {formatNumber(r.system_outstanding_chf)} · Δ {r.delta_chf > 0 ? '+' : ''}{formatNumber(r.delta_chf)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {(recon.matched_count ?? 0) > recon.linked_in_bexio.length ? (
                    <p className="mt-2 text-[11px] italic text-gray-600">
                      Showing first {recon.linked_in_bexio.length} of {recon.matched_count}; remaining rows truncated for payload size.
                    </p>
                  ) : null}
                </details>
              ) : null}

              <BexioReconSection
                title="Unmapped currencies (excluded from sum)"
                tone="gray"
                count={recon.unmapped_count ?? (recon.unmapped_currencies?.length || 0)}
                previewLen={recon.unmapped_currencies?.length || 0}
              >
                <ul className="divide-y divide-gray-100">
                  {recon.unmapped_currencies.map((r) => (
                    <li key={`uc-${r.invoice_id}`} className="flex items-center justify-between py-1.5 text-xs">
                      <span className="font-mono text-gray-700">{r.document_nr || `#${r.invoice_id}`}</span>
                      <span className="tabular-nums text-gray-700">{formatNumber(r.remaining_raw)} {r.currency_code}</span>
                    </li>
                  ))}
                </ul>
              </BexioReconSection>

              {!((recon.drift_count || 0)
                || (recon.unlinked_count ?? recon.unlinked_in_bexio?.length ?? 0)
                || (recon.missing_count ?? recon.missing_in_bexio?.length ?? 0)
                || (recon.orphan_count ?? recon.orphan_tranches?.length ?? 0)
                || (recon.unmapped_count ?? recon.unmapped_currencies?.length ?? 0)) ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  All tracked invoices reconcile with Bexio within tolerance. Nothing to action.
                </div>
              ) : null}
            </div>
          ) : null}
          {recon?.state === 'error' ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {recon.message || 'Reconciliation failed.'}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function InfoHint({ text }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help text-gray-400 hover:text-gray-600">
            <CircleHelp className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ForecastTab({ onOpenBucketDeals, onOpenDeal }) {
  const [data, setData] = useState(null);
  const [annualTarget, setAnnualTarget] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [monthlyPerf, setMonthlyPerf] = useState(null);
  const [receivablesSummary, setReceivablesSummary] = useState(null);
  const [receivablesByClient, setReceivablesByClient] = useState(null);
  const [receivablesByDeal, setReceivablesByDeal] = useState(null);
  const [receivablesByMonth, setReceivablesByMonth] = useState(null);
  const [receivablesByProject, setReceivablesByProject] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [yearFilter, setYearFilter] = useState(defaultYearFilter);
  const [fromMonth, setFromMonth] = useState(defaultFromMonth);
  const [toMonth, setToMonth] = useState(defaultToMonth);
  const [arClientFilter, setArClientFilter] = useState('');
  const [arDealFilter, setArDealFilter] = useState('');
  const [arEntityFilter, setArEntityFilter] = useState('');
  const [arMonthFilter, setArMonthFilter] = useState('');
  const [arDrillMode, setArDrillMode] = useState('all');

  // Bexio reconciliation — lazy-loaded once on mount; the drawer offers a
  // manual refresh button that hits ?refresh=1 to bypass the server cache.
  // We never block the dashboard on this (it's a cross-check overlay, not
  // the source of truth for the KPIs).
  const [bexioRecon, setBexioRecon] = useState(null);
  const [bexioReconLoading, setBexioReconLoading] = useState(false);
  const [bexioReconError, setBexioReconError] = useState('');
  const [bexioReconOpen, setBexioReconOpen] = useState(false);

  const [kpiExplainOpen, setKpiExplainOpen] = useState(false);
  const [kpiExplainLoading, setKpiExplainLoading] = useState(false);
  const [kpiExplainData, setKpiExplainData] = useState(null);
  const [kpiExplainMetric, setKpiExplainMetric] = useState(null);

  const [historyGranularity, setHistoryGranularity] = useState('daily');
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');

  const queryParams = useMemo(() => {
    const params = {};
    if (yearFilter !== 'all') params.year = Number(yearFilter);
    if (fromMonth && toMonth) {
      params.from_month = fromMonth;
      params.to_month = toMonth;
    }
    return params;
  }, [yearFilter, fromMonth, toMonth]);

  const annualTargetParams = useMemo(() => {
    if (yearFilter === 'all') return null;
    const params = { year: Number(yearFilter) };
    if (fromMonth && toMonth) {
      params.from_month = fromMonth;
      params.to_month = toMonth;
    }
    return params;
  }, [yearFilter, fromMonth, toMonth]);

  // Receivables are company-wide AR: do not inherit forecast `year` / `from_month` / `to_month`,
  // or selecting a forecast year would silently filter AR to the deal reference month bucket for that year only
  // (understating real open AR). Slice AR only via the AR month input and row filters below.
  const receivablesQueryParams = useMemo(() => {
    const params = {};
    if (arClientFilter.trim()) params.client_name = arClientFilter.trim();
    if (arDealFilter.trim()) params.deal_id = arDealFilter.trim();
    if (arEntityFilter.trim()) params.entity = arEntityFilter.trim();
    if (arMonthFilter) params.month = arMonthFilter;
    return params;
  }, [arClientFilter, arDealFilter, arEntityFilter, arMonthFilter]);

  const hasHalfCustomRange = (fromMonth && !toMonth) || (!fromMonth && toMonth);

  // Resolve the effective period window (YYYY-MM .. YYYY-MM) used to fill in
  // empty months so the tables/charts always span the full selected range (e.g.
  // January through December for a calendar-year view) rather than only months
  // that happen to carry forecast or actual data.
  const resolvedPeriod = useMemo(() => {
    if (fromMonth && toMonth) return { from: fromMonth, to: toMonth };
    if (yearFilter !== 'all') return { from: `${yearFilter}-01`, to: `${yearFilter}-12` };
    return { from: null, to: null };
  }, [yearFilter, fromMonth, toMonth]);

  const periodMonths = useMemo(
    () => enumerateMonths(resolvedPeriod.from, resolvedPeriod.to),
    [resolvedPeriod],
  );

  // Deal Library drill for the "Outside selected period" rollup must send the
  // same forecast window the UI used to build that row so the backend can
  // union months *outside* ``period_from``..``period_to`` (see
  // ``bucket=outside_period`` on ``/forecast/bucket-deals``).
  const receivablesOutsideDrillFilters = useMemo(() => {
    const params = { ...receivablesQueryParams };
    if (resolvedPeriod.from && resolvedPeriod.to) {
      params.period_from = resolvedPeriod.from;
      params.period_to = resolvedPeriod.to;
    }
    return params;
  }, [receivablesQueryParams, resolvedPeriod.from, resolvedPeriod.to]);

  const kpiExplainTitle =
    kpiExplainData?.metric_label
    || (kpiExplainMetric === 'committed_revenue'
      ? 'Committed revenue'
      : kpiExplainMetric === 'weighted_revenue'
        ? 'Sales forecast'
        : kpiExplainMetric === 'forecast_margin'
          ? 'Forecast margin (portfolio)'
          : 'Forecast KPI');

  const loadForecast = useCallback(async () => {
    if (hasHalfCustomRange) {
      setHistoryPoints([]);
      setHistoryError('');
      setHistoryLoading(false);
      setLoading(false);
      setAnnualTarget(null);
      return;
    }
    setLoading(true);
    setHistoryLoading(true);
    setError('');
    setHistoryError('');
    try {
      const historyParams = { ...queryParams, granularity: historyGranularity };
      const coreRequests = Promise.all([
        getForecastSummary(queryParams),
        getForecastTimeline(queryParams),
        getForecastMonthlyPerformance(queryParams),
        getReceivablesSummary(receivablesQueryParams),
        getReceivablesByClient(receivablesQueryParams),
        getReceivablesByDeal(receivablesQueryParams),
        getReceivablesByMonth(receivablesQueryParams),
        getReceivablesByProject(receivablesQueryParams),
        getForecastHistory(historyParams),
      ]);
      const targetRequest = annualTargetParams
        ? getForecastAnnualTarget(annualTargetParams).catch(() => ({ data: null }))
        : Promise.resolve({ data: null });
      const [coreRes, targetRes] = await Promise.all([coreRequests, targetRequest]);
      const [sumRes, tlRes, perfRes, rsRes, rcRes, rdRes, rmRes, rpRes, histRes] = coreRes;
      setData(sumRes.data);
      setAnnualTarget(targetRes.data);
      setTimeline(tlRes.data);
      setMonthlyPerf(perfRes.data);
      setReceivablesSummary(rsRes.data);
      setReceivablesByClient(rcRes.data);
      setReceivablesByDeal(rdRes.data);
      setReceivablesByMonth(rmRes.data);
      setReceivablesByProject(rpRes.data);
      setHistoryPoints(histRes.data?.points || []);
    } catch (e) {
      setError('Could not load forecast data.');
      setHistoryPoints([]);
      setAnnualTarget(null);
      setHistoryError('Could not load forecast history.');
    } finally {
      setLoading(false);
      setHistoryLoading(false);
    }
  }, [queryParams, receivablesQueryParams, historyGranularity, hasHalfCustomRange, annualTargetParams]);

  // Fetch Bexio reconciliation. Kept independent of ``loadForecast`` so a
  // Bexio outage never prevents the dashboard from rendering — the badge
  // simply falls back to a muted "unavailable" state.
  // ``refresh: true`` → POST sync-paid (mutations) then uses that payload.
  // ``refresh: false`` → GET read-only (cache-friendly).
  const loadBexioRecon = useCallback(async ({ refresh = false } = {}) => {
    setBexioReconLoading(true);
    setBexioReconError('');
    try {
      const res = refresh
        ? await syncBexioReconciliationPaid()
        : await getBexioReconciliation({ refresh: false });
      setBexioRecon(res.data);
      const synced = Number(res.data?.auto_synced_paid_count || 0);
      const failed = Number(res.data?.auto_sync_failed_count || 0);
      if (synced > 0) {
        toast.success(
          synced === 1
            ? 'Bexio Paid synced into the ledger'
            : `${synced} Bexio Paid invoices synced into the ledger`
        );
        // Ledger changed — refresh Forecast AR KPIs so Invoiced/Total update.
        loadForecast();
      }
      if (failed > 0) {
        toast.warning(
          failed === 1
            ? 'Could not auto-sync one Bexio Paid invoice'
            : `Could not auto-sync ${failed} Bexio Paid invoices`
        );
      }
    } catch (e) {
      setBexioReconError('Could not reach Bexio reconciliation endpoint.');
    } finally {
      setBexioReconLoading(false);
    }
  }, [loadForecast]);

  const cleanupOrphans = useCallback(async () => {
    try {
      const res = await cleanupBexioReconOrphans();
      const t = res.data?.tranches_deleted || 0;
      const p = res.data?.payments_deleted || 0;
      const c = res.data?.tranches_cleared || 0;
      const parts = [];
      if (t) parts.push(`deleted ${t} deleted-deal tranche${t === 1 ? '' : 's'}${p ? ` (+${p} payment${p === 1 ? '' : 's'})` : ''}`);
      if (c) parts.push(`cleared ${c} stale maintenance link${c === 1 ? '' : 's'}`);
      toast.success(parts.length ? parts.join('; ') : 'No orphan tranches to clean up.');
      await loadBexioRecon({ refresh: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Orphan cleanup failed.');
    }
  }, [loadBexioRecon]);

  useEffect(() => {
    // POST sync-paid on Forecast entry so Bexio Paid → ledger applies without
    // opening each deal (GET recon stays read-only).
    loadBexioRecon({ refresh: true });
  }, [loadBexioRecon]);

  useEffect(() => {
    if (hasHalfCustomRange) return;
    loadForecast();
  }, [loadForecast, hasHalfCustomRange]);

  // After editing a deal elsewhere (e.g. Service Rendered Date), returning to
  // this tab should reload receivables-by-month instead of showing stale buckets.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && !hasHalfCustomRange) {
        loadForecast();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadForecast, hasHalfCustomRange]);

  const maxPipeline = data
    ? Math.max(...DEAL_STAGE_VALUES_ORDERED.map((s) => data.pipeline_by_stage[s] ?? 0), 1)
    : 1;

  const displayedMonthlyRows = useMemo(() => {
    const raw = monthlyPerf?.rows ?? [];
    if (!periodMonths) return raw;
    const byMonth = new Map(raw.map((r) => [r.month, r]));
    return periodMonths.map((m) => byMonth.get(m) ?? {
      month: m,
      opening_forecast_chf: null,
      projected_sales_chf: 0,
      actual_revenue_chf: 0,
      delta_chf: null,
      delta_pct: null,
    });
  }, [monthlyPerf, periodMonths]);

  const monthlyPerfTotals = useMemo(() => {
    let opening = 0;
    let projected = 0;
    let actual = 0;
    let delta = 0;
    for (const r of displayedMonthlyRows) {
      if (r.opening_forecast_chf !== null && r.opening_forecast_chf !== undefined) {
        opening += Number(r.opening_forecast_chf || 0);
      }
      // Keep totals aligned with what the table visibly shows.
      if (!isPastMonth(r.month)) {
        projected += Number(r.projected_sales_chf || 0);
      }
      actual += Number(r.actual_revenue_chf || 0);
      if (r.delta_chf !== null && r.delta_chf !== undefined) {
        delta += Number(r.delta_chf || 0);
      }
    }
    projected += Number(monthlyPerf?.unscheduled_projected_sales_chf || 0);
    return {
      opening,
      projected,
      actual,
      delta,
      deltaPct: opening ? (delta / opening) * 100 : null,
    };
  }, [displayedMonthlyRows, monthlyPerf]);

  // Distribute open AR across the forecast period using due-date month buckets.
  // Overdue receivables stay anchored to their original due month; anything
  // falling outside the selected window rolls up into a greyed "Outside period"
  // footer row so the visible totals reconcile to Total Receivables.
  const receivablesMonthlyBreakdown = useMemo(() => {
    const rows = receivablesByMonth?.rows ?? [];
    if (!periodMonths) {
      return {
        rows: rows.map((r) => ({ ...r })),
        outside: null,
      };
    }
    const byMonth = new Map(rows.map((r) => [r.month, r]));
    const windowSet = new Set(periodMonths);
    const displayed = periodMonths.map((m) => byMonth.get(m) ?? {
      month: m,
      total_chf: 0,
      overdue_chf: 0,
      invoice_count: 0,
      deal_ids: [],
      invoices: [],
    });
    let outsideTotal = 0;
    let outsideOverdue = 0;
    let outsideCount = 0;
    const outsideInvoices = [];
    const outsideDealIds = new Set();
    for (const r of rows) {
      if (windowSet.has(r.month)) continue;
      outsideTotal += r.total_chf || 0;
      outsideOverdue += r.overdue_chf || 0;
      outsideCount += r.invoice_count || 0;
      for (const inv of r.invoices || []) outsideInvoices.push(inv);
      for (const did of r.deal_ids || []) outsideDealIds.add(did);
    }
    return {
      rows: displayed,
      outside: outsideCount > 0 || outsideTotal > 0
        ? {
            total_chf: outsideTotal,
            overdue_chf: outsideOverdue,
            invoice_count: outsideCount,
            deal_ids: Array.from(outsideDealIds),
            invoices: outsideInvoices,
          }
        : null,
    };
  }, [receivablesByMonth, periodMonths]);

  // Expanded-state per month (YYYY-MM). Default collapsed so the table stays
  // compact; expand on click to reveal the per-invoice chip list.
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const toggleMonthExpanded = useCallback((key) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Open-AR KPI drill-down: filter + show the slice that feeds the active KPI.
  // Slice amounts come from /ar/by-project (invoiced_outstanding_chf,
  // uninvoiced_amount_chf, overdue_chf, outstanding_chf) — not recomputed here.
  const isMaintenanceProject = useCallback(
    (row) => String(row?.deal_id || '').startsWith('maintenance:'),
    [],
  );

  const projectKpiAmount = useCallback((row) => {
    if (arDrillMode === 'overdue') return Math.max(0, Number(row?.overdue_chf) || 0);
    if (arDrillMode === 'invoiced') return Math.max(0, Number(row?.invoiced_outstanding_chf) || 0);
    if (arDrillMode === 'uninvoiced') return Math.max(0, Number(row?.uninvoiced_amount_chf) || 0);
    return Math.max(0, Number(row?.outstanding_chf) || 0);
  }, [arDrillMode]);

  const projectIssuedForDrill = useCallback((row) => {
    const issued = row?.issued_invoices || [];
    if (arDrillMode === 'overdue') {
      return issued.filter((inv) => (Number(inv.days_overdue) || 0) > 0 || inv.status === 'overdue');
    }
    if (arDrillMode === 'uninvoiced') {
      return [];
    }
    return issued;
  }, [arDrillMode]);

  const arKpiAmountLabel = useMemo(() => {
    if (arDrillMode === 'overdue') return 'Overdue';
    if (arDrillMode === 'invoiced') return 'Invoiced (open)';
    if (arDrillMode === 'uninvoiced') return 'Uninvoiced (open)';
    return 'Outstanding';
  }, [arDrillMode]);

  const displayedProjectRows = useMemo(() => {
    const rows = receivablesByProject?.rows ?? [];
    if (arDrillMode === 'overdue') {
      return rows.filter((r) => (r.overdue_chf || 0) > 0.005);
    }
    if (arDrillMode === 'invoiced') {
      return rows.filter((r) => (r.invoiced_outstanding_chf || 0) > 0.005);
    }
    if (arDrillMode === 'uninvoiced') {
      return rows.filter((r) => (r.uninvoiced_amount_chf || 0) > 0.005);
    }
    return rows;
  }, [receivablesByProject, arDrillMode]);

  const displayedProjectTotals = useMemo(() => {
    let dv = 0;
    let inv = 0;
    let paid = 0;
    let kpiSum = 0;
    let portfolioKpiSum = 0;
    let maintKpiSum = 0;
    let ov = 0;
    for (const r of displayedProjectRows) {
      dv += r.deal_value_chf || 0;
      inv += r.invoiced_chf || 0;
      paid += r.paid_chf || 0;
      const amt = projectKpiAmount(r);
      kpiSum += amt;
      ov += r.overdue_chf || 0;
      if (isMaintenanceProject(r)) maintKpiSum += amt;
      else portfolioKpiSum += amt;
    }
    return {
      deal_value_chf: dv,
      invoiced_chf: inv,
      paid_chf: paid,
      outstanding_chf: kpiSum,
      portfolio_kpi_chf: portfolioKpiSum,
      maintenance_kpi_chf: maintKpiSum,
      overdue_chf: ov,
      invoiced_percentage: dv > 0 ? Number(((inv / dv) * 100).toFixed(1)) : 0,
    };
  }, [displayedProjectRows, projectKpiAmount, isMaintenanceProject]);

  // Rebuild by-client from the drilled project rows so the list matches the KPI.
  const displayedClientRows = useMemo(() => {
    const byClient = new Map();
    for (const row of displayedProjectRows) {
      const name = row.client_name || 'Unknown';
      byClient.set(name, (byClient.get(name) || 0) + projectKpiAmount(row));
    }
    return [...byClient.entries()]
      .map(([client_name, total_chf]) => ({ client_name, total_chf }))
      .sort((a, b) => b.total_chf - a.total_chf)
      .slice(0, 8);
  }, [displayedProjectRows, projectKpiAmount]);

  const arDrillEmptyMessage = useMemo(() => {
    if (arDrillMode === 'overdue') return 'No projects with overdue invoices in the current filter.';
    if (arDrillMode === 'invoiced') return 'No projects with invoiced open AR in the current filter.';
    if (arDrillMode === 'uninvoiced') return 'No projects with uninvoiced open AR in the current filter.';
    return 'No projects with open receivables in the current filter.';
  }, [arDrillMode]);

  const arDrillKpiMatchChf = useMemo(() => {
    if (!receivablesSummary) return null;
    if (arDrillMode === 'overdue') return receivablesSummary.overdue_receivables_chf;
    if (arDrillMode === 'invoiced') return receivablesSummary.invoiced_amount_chf;
    if (arDrillMode === 'uninvoiced') return receivablesSummary.uninvoiced_amount_chf ?? 0;
    return receivablesSummary.total_receivables_chf;
  }, [arDrillMode, receivablesSummary]);

  const asOfDateLabel = useMemo(() => {
    const raw = monthlyPerf?.as_of_date;
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    return new Date().toISOString().slice(0, 10);
  }, [monthlyPerf]);

  const openKpiExplain = useCallback(async (metric) => {
    setKpiExplainMetric(metric);
    setKpiExplainOpen(true);
    setKpiExplainLoading(true);
    setKpiExplainData(null);
    try {
      const res = await getForecastKpiExplain(metric, queryParams);
      setKpiExplainData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Could not load KPI breakdown');
      setKpiExplainOpen(false);
      setKpiExplainMetric(null);
    } finally {
      setKpiExplainLoading(false);
    }
  }, [queryParams]);

  const applyPreset = (preset) => {
    if (preset === 'all') {
      setYearFilter('all');
      setFromMonth('');
      setToMonth('');
      return;
    }
    if (preset === 'this_year') {
      const y = String(new Date().getFullYear());
      if (YEAR_OPTIONS.some((o) => o.value === y)) {
        setYearFilter(y);
        setFromMonth('');
        setToMonth('');
      } else {
        setYearFilter('all');
        setFromMonth(`${y}-01`);
        setToMonth(`${y}-12`);
      }
      return;
    }
    if (preset === 'next_year') {
      const y = String(new Date().getFullYear() + 1);
      if (YEAR_OPTIONS.some((o) => o.value === y)) {
        setYearFilter(y);
        setFromMonth('');
        setToMonth('');
      } else {
        setYearFilter('all');
        setFromMonth(`${y}-01`);
        setToMonth(`${y}-12`);
      }
      return;
    }
    if (preset === 'last_12') {
      const end = currentMonthKey();
      const start = addMonths(end, -11);
      setYearFilter('all');
      setFromMonth(start);
      setToMonth(end);
    }
  };

  const kpiCardClass = 'pl-kpi-card !min-h-[86px] p-3 touch-manipulation active:bg-gray-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400';

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-auto bg-[var(--color-background)] pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-4 sm:p-6"
      data-testid="forecast-tab"
    >
      <div className="mx-auto w-full max-w-5xl space-y-4 md:space-y-4 xl:max-w-6xl">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="pl-page-title">Forecast</h1>
            <InfoHint text="Portfolio view in CHF. Sales forecast uses stage probabilities. Monthly actuals use paid revenue recognized up to today." />
          </div>
          <p className="mt-2 inline-flex rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold tracking-wide text-sky-800" data-testid="forecast-asof-date">
            Today / As-of date: {asOfDateLabel}
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm" data-testid="forecast-period-controls">
          <div className="grid gap-2 md:flex md:flex-nowrap md:items-end">
            <div className="space-y-1 md:w-[180px] md:min-w-[170px]">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Year</p>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:w-[180px] md:min-w-[170px]">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">From</p>
              <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1 md:w-[180px] md:min-w-[170px]">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">To</p>
              <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1 md:flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Presets</p>
              <div className="flex flex-wrap gap-1 md:flex-nowrap">
                <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => applyPreset('this_year')}>This year</Button>
                <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => applyPreset('next_year')}>Next year</Button>
                <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => applyPreset('last_12')}>Last 12m</Button>
                <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => applyPreset('all')}>All</Button>
              </div>
            </div>
          </div>
          {hasHalfCustomRange ? (
            <p className="mt-2 text-xs text-amber-700">Set both From and To months to apply a custom period.</p>
          ) : null}
        </div>

        {loading && (
          <p className="text-sm text-gray-500" data-testid="forecast-loading">Loading…</p>
        )}
        {error && !loading && (
          <p className="text-sm text-red-600" data-testid="forecast-error">{error}</p>
        )}

        {!loading && data && timeline && monthlyPerf && receivablesSummary && receivablesByClient && receivablesByDeal && receivablesByMonth && receivablesByProject && (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2">
              <button type="button" className={kpiCardClass} data-testid="forecast-card-committed" onClick={() => openKpiExplain('committed_revenue')}>
                <p className="pl-kpi-label">Committed revenue</p>
                <p className="pl-kpi-value">
                  {formatNumber(data.committed_revenue_chf)} <span className="text-[11px] font-normal text-gray-500">CHF</span>
                </p>
              </button>
              <button type="button" className={kpiCardClass} data-testid="forecast-card-weighted" onClick={() => openKpiExplain('weighted_revenue')}>
                <p className="pl-kpi-label">Sales Forecast</p>
                <p className="pl-kpi-value">
                  {formatNumber(data.weighted_revenue_chf)} <span className="text-[11px] font-normal text-gray-500">CHF</span>
                </p>
              </button>
            </div>

            <ForecastAnnualTargetCard data={annualTarget} onSaved={loadForecast} />

            <ForecastHistoryChart
              points={historyPoints}
              granularity={historyGranularity}
              onGranularityChange={setHistoryGranularity}
              loading={historyLoading}
              error={historyError}
            />

            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:rounded-lg">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Pipeline by stage (CHF revenue)</p>
                <p className="text-[11px] text-gray-400">{data.deal_count} deal{data.deal_count !== 1 ? 's' : ''}</p>
              </div>
              <ul className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2" data-testid="forecast-pipeline-by-stage">
                {DEAL_STAGE_VALUES_ORDERED.map((stage) => {
                  const v = data.pipeline_by_stage[stage] ?? 0;
                  const pct = Math.round((v / maxPipeline) * 100);
                  return (
                    <li key={stage} className="flex items-center gap-2 text-xs" data-testid={`forecast-stage-row-${stage}`}>
                      <span className="w-20 shrink-0 truncate font-medium text-gray-700 sm:w-[4.5rem]">{getStageLabel(stage)}</span>
                      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`w-[5.25rem] shrink-0 text-right font-mono text-[10px] tabular-nums text-gray-600 sm:text-[11px] ${negativePurpleClass(v)}`}>
                        {formatNumber(v)} CHF
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:rounded-lg sm:p-4">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Monthly forecast vs actual (CHF)</p>
                <InfoHint text={`Opening Forecast: benchmark captured on the 1st of the month (Europe/Zurich); frozen thereafter. Projected Sales: stage-weighted revenue still to close for the current and future months — Demand and Quote Issued only (stalled pipeline excluded); hidden for past months. Actual Revenue: CHF total of deals in Approved/Partial/Invoiced/Paid whose Deal Closure Date falls in that month, once the close date is on or before ${monthlyPerf.as_of_date} (UTC) or ${monthlyPerf.as_of_date} is still inside that month (intra-month approvals). May still change if deal values are edited. Delta = Actual − Opening Forecast. Unscheduled row: Demand and Quote Issued stage-weighted revenue with no Deal Closure Date.`} />
              </div>
              <div className="mt-3 space-y-2 md:hidden">
                {displayedMonthlyRows.length === 0 ? (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                    No months in selected period.
                  </div>
                ) : (
                  <>
                    {displayedMonthlyRows.map((r) => {
                      const hasOpening = r.opening_forecast_chf !== null && r.opening_forecast_chf !== undefined;
                      const hasDelta = r.delta_chf !== null && r.delta_chf !== undefined;
                      return (
                        <button
                          key={`mobile-${r.month}`}
                          type="button"
                          className="w-full rounded-md border border-gray-200 bg-white px-3 py-3 text-left shadow-sm active:bg-gray-50"
                          onClick={() => onOpenBucketDeals?.({ view: 'monthly_performance', bucket: r.month })}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-800">{formatMonthLabel(r.month)}</p>
                            <span className={`text-xs font-semibold ${deltaColorClass(hasDelta ? r.delta_chf : null)}`}>
                              {hasDelta ? deltaPct(r.opening_forecast_chf, r.delta_chf) : '—'}
                            </span>
                          </div>
                          <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <dt className="text-gray-400">Opening</dt>
                              <dd className="font-mono text-gray-800">{hasOpening ? `${formatNumber(r.opening_forecast_chf)} CHF` : '—'}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">Projected</dt>
                              <dd className={`font-mono ${isPastMonth(r.month) ? 'text-gray-400' : 'text-gray-800'}`}>
                                {isPastMonth(r.month) ? '—' : `${formatNumber(r.projected_sales_chf)} CHF`}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">Actual</dt>
                              <dd className="font-mono text-gray-800">{formatNumber(r.actual_revenue_chf)} CHF</dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">Delta</dt>
                              <dd className={`font-mono ${deltaColorClass(hasDelta ? r.delta_chf : null)}`}>
                                {hasDelta ? `${formatNumber(r.delta_chf)} CHF` : '—'}
                              </dd>
                            </div>
                          </dl>
                        </button>
                      );
                    })}
                    <button
                      key="mobile-unscheduled"
                      type="button"
                      className="w-full rounded-md border border-gray-200 bg-gray-50/60 px-3 py-3 text-left text-gray-500 shadow-sm active:bg-gray-100"
                      onClick={() => onOpenBucketDeals?.({ view: 'monthly_performance', bucket: 'unscheduled' })}
                      data-testid="forecast-monthly-card-unscheduled"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold italic text-gray-600">Unscheduled</p>
                        <span className="text-xs text-gray-400">—</span>
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <dt className="text-gray-400">Opening</dt>
                          <dd className="font-mono text-gray-400">—</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Projected</dt>
                          <dd className="font-mono text-gray-600">{formatNumber(monthlyPerf.unscheduled_projected_sales_chf)} CHF</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Actual</dt>
                          <dd className="font-mono text-gray-400">—</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Delta</dt>
                          <dd className="font-mono text-gray-400">—</dd>
                        </div>
                      </dl>
                    </button>
                    <div
                      key="mobile-total"
                      className="w-full rounded-md border-2 border-gray-300 bg-gray-50 px-3 py-3 text-left text-gray-700 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold">Total</p>
                        <span className={`text-xs font-semibold ${deltaColorClass(monthlyPerfTotals.delta)}`}>
                          {monthlyPerfTotals.deltaPct !== null ? formatPercent(monthlyPerfTotals.deltaPct) : '—'}
                        </span>
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <dt className="text-gray-500">Opening</dt>
                          <dd className="font-mono text-gray-800">{formatNumber(monthlyPerfTotals.opening)} CHF</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Projected</dt>
                          <dd className="font-mono text-gray-800">{formatNumber(monthlyPerfTotals.projected)} CHF</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Actual</dt>
                          <dd className="font-mono text-gray-800">{formatNumber(monthlyPerfTotals.actual)} CHF</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Delta</dt>
                          <dd className={`font-mono ${deltaColorClass(monthlyPerfTotals.delta)}`}>
                            {formatNumber(monthlyPerfTotals.delta)} CHF
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </>
                )}
              </div>
              <div className="pl-table-shell mt-3 hidden overflow-x-auto md:block">
                <table className="w-full min-w-[780px] text-sm">
                  <thead className="pl-table-header bg-white text-xs text-gray-500">
                    <tr>
                      <th className="pl-table-head-cell text-left">Month</th>
                      <th className="pl-table-head-cell text-right">Opening Forecast</th>
                      <th className="pl-table-head-cell text-right">Projected Sales</th>
                      <th className="pl-table-head-cell text-right">Actual Revenue</th>
                      <th className="pl-table-head-cell text-right">Delta</th>
                      <th className="pl-table-head-cell text-right">Delta %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedMonthlyRows.length === 0 ? (
                      <tr><td className="px-3 py-4 text-sm text-gray-500" colSpan={6}>No months in selected period.</td></tr>
                    ) : (
                      <>
                        {displayedMonthlyRows.map((r) => {
                          const hasOpening = r.opening_forecast_chf !== null && r.opening_forecast_chf !== undefined;
                          const hasDelta = r.delta_chf !== null && r.delta_chf !== undefined;
                          return (
                            <tr
                              key={r.month}
                              className="pl-row cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                              onClick={() => onOpenBucketDeals?.({ view: 'monthly_performance', bucket: r.month })}
                            >
                              <td className="pl-cell-text">{formatMonthLabel(r.month)}</td>
                              <td className={`pl-cell-num ${hasOpening ? negativePurpleClass(r.opening_forecast_chf) : 'text-gray-400'}`}>
                                {hasOpening ? `${formatNumber(r.opening_forecast_chf)} CHF` : '—'}
                              </td>
                              <td className={`pl-cell-num ${isPastMonth(r.month) ? 'text-gray-400' : negativePurpleClass(r.projected_sales_chf)}`}>
                                {isPastMonth(r.month) ? '—' : `${formatNumber(r.projected_sales_chf)} CHF`}
                              </td>
                              <td className={`pl-cell-num ${negativePurpleClass(r.actual_revenue_chf)}`}>{formatNumber(r.actual_revenue_chf)} CHF</td>
                              <td className={`pl-cell-num ${deltaColorClass(hasDelta ? r.delta_chf : null)}`}>
                                {hasDelta ? `${formatNumber(r.delta_chf)} CHF` : '—'}
                              </td>
                              <td className={`pl-cell-num ${deltaColorClass(hasDelta ? r.delta_chf : null)}`}>
                                {hasDelta ? deltaPct(r.opening_forecast_chf, r.delta_chf) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                        <tr
                          key="unscheduled"
                          className="pl-row cursor-pointer border-t border-gray-100 bg-gray-50/60 text-gray-500 hover:bg-gray-100"
                          onClick={() => onOpenBucketDeals?.({ view: 'monthly_performance', bucket: 'unscheduled' })}
                          data-testid="forecast-monthly-row-unscheduled"
                        >
                          <td className="pl-cell-text italic text-gray-500">Unscheduled</td>
                          <td className="pl-cell-num text-gray-400">—</td>
                          <td className="pl-cell-num !text-gray-500">{formatNumber(monthlyPerf.unscheduled_projected_sales_chf)} CHF</td>
                          <td className="pl-cell-num text-gray-400">—</td>
                          <td className="pl-cell-num text-gray-400">—</td>
                          <td className="pl-cell-num text-gray-400">—</td>
                        </tr>
                        <tr
                          key="totals"
                          className="border-t-2 border-gray-300 bg-gray-50/90 font-semibold text-gray-800"
                          data-testid="forecast-monthly-row-totals"
                        >
                          <td className="pl-cell-text">Total</td>
                          <td className="pl-cell-num">{formatNumber(monthlyPerfTotals.opening)} CHF</td>
                          <td className="pl-cell-num">{formatNumber(monthlyPerfTotals.projected)} CHF</td>
                          <td className="pl-cell-num">{formatNumber(monthlyPerfTotals.actual)} CHF</td>
                          <td className={`pl-cell-num ${deltaColorClass(monthlyPerfTotals.delta)}`}>
                            {formatNumber(monthlyPerfTotals.delta)} CHF
                          </td>
                          <td className={`pl-cell-num ${deltaColorClass(monthlyPerfTotals.delta)}`}>
                            {monthlyPerfTotals.deltaPct !== null ? formatPercent(monthlyPerfTotals.deltaPct) : '—'}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:rounded-lg sm:p-4" data-testid="receivables-dashboard">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Receivables dashboard (CHF)</p>
                  <InfoHint text="Open receivables = contractual cash-in minus ledger payments. Paid stage wins (zero outstanding), avoiding double-counting. Bexio remains reconciliation-only. Totals ignore the forecast period above; use the reference month filter here for month slicing." />
                </div>
                <BexioReconBadge
                  recon={bexioRecon}
                  loading={bexioReconLoading}
                  error={bexioReconError}
                  onClick={() => setBexioReconOpen(true)}
                />
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  type="button"
                  className={kpiCardClass}
                  data-testid="receivables-card-invoiced"
                  onClick={() => setArDrillMode('invoiced')}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="pl-kpi-label">Invoiced (open)</p>
                    <InfoHint text="Billed AR still owed: outstanding on issued / partially paid / overdue Bexio invoices. Not cumulative ever-invoiced (that is Deal Library Invoiced %). Overdue is a subset of this amount." />
                    {receivablesSummary.invoiced_pct_stale ? (
                      <span
                        className="rounded-sm bg-amber-100 px-1 py-0 text-[10px] font-semibold uppercase text-amber-800"
                        title="Invoiced open AR could not be computed from the tranche ledger (aggregation failed). Displayed value may be incomplete."
                        data-testid="receivables-invoiced-stale-badge"
                      >
                        stale
                      </span>
                    ) : null}
                  </div>
                  <p className="pl-kpi-value">{formatNumber(receivablesSummary.invoiced_amount_chf ?? 0)} CHF</p>
                  <p className="mt-1 text-[10px] text-gray-500">
                    {(receivablesSummary.invoiced_percentage ?? 0).toFixed(1)}% of open AR
                    {arDrillMode === 'invoiced' ? ' · Drill-down active' : ''}
                  </p>
                </button>
                <button
                  type="button"
                  className={kpiCardClass}
                  data-testid="receivables-card-uninvoiced"
                  onClick={() => setArDrillMode('uninvoiced')}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="pl-kpi-label">Uninvoiced (open)</p>
                    <InfoHint text="Committed contract balance not yet billed. Equals Total receivables minus Invoiced (open). Includes amounts whose billing window may already be past (missing invoice), not only calendar-future cash." />
                  </div>
                  <p className="pl-kpi-value">{formatNumber(receivablesSummary.uninvoiced_amount_chf ?? 0)} CHF</p>
                  <p className="mt-1 text-[10px] text-gray-500">
                    {arDrillMode === 'uninvoiced' ? 'Drill-down active' : 'Click to drill-down'}
                  </p>
                </button>
                <button
                  type="button"
                  className={kpiCardClass}
                  data-testid="receivables-card-total"
                  onClick={() => setArDrillMode('all')}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="pl-kpi-label">Total receivables</p>
                    <InfoHint text="Open AR = deal contract minus payments. Equals Invoiced (open) + Uninvoiced (open). Not the full contractual book (see the Contractual = Paid + Outstanding strip)." />
                  </div>
                  <p className="pl-kpi-value">{formatNumber(receivablesSummary.total_receivables_chf)} CHF</p>
                  <p className="mt-1 text-[10px] text-gray-500">{arDrillMode === 'all' ? 'Drill-down active' : 'Click to drill-down'}</p>
                </button>
                <button
                  type="button"
                  className={kpiCardClass}
                  data-testid="receivables-card-overdue"
                  onClick={() => setArDrillMode('overdue')}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="pl-kpi-label">Overdue</p>
                    <InfoHint text="Past-due portion of Invoiced (open): outstanding on Bexio invoices whose payment due date is before today. Uninvoiced never counts as overdue (no invoice payment clock)." />
                  </div>
                  <p className="pl-kpi-value">{formatNumber(receivablesSummary.overdue_receivables_chf)} CHF</p>
                  <p className="mt-1 text-[10px] text-gray-500">{arDrillMode === 'overdue' ? 'Drill-down active' : 'Click to drill-down'}</p>
                </button>
              </div>

              <div
                className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs tabular-nums text-gray-700"
                data-testid="receivables-open-ar-partition-strip"
              >
                <span>
                  Invoiced {formatNumber(receivablesSummary.invoiced_amount_chf ?? 0)} CHF + Uninvoiced{' '}
                  {formatNumber(receivablesSummary.uninvoiced_amount_chf ?? 0)} CHF = Total{' '}
                  {formatNumber(receivablesSummary.total_receivables_chf)} CHF
                </span>
                <span className="ml-2 text-[11px] text-gray-500">
                  · Overdue {formatNumber(receivablesSummary.overdue_receivables_chf)} CHF ⊆ invoiced
                </span>
              </div>

              <div
                className={`mt-2 rounded-lg border px-3 py-2 text-xs tabular-nums ${
                  receivablesSummary.reconciles
                    ? 'border-gray-100 bg-gray-50 text-gray-700'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
                data-testid="receivables-reconciliation-strip"
              >
                <span>
                  Contractual {formatNumber(receivablesSummary.contractual_amount_chf)} CHF = Paid{' '}
                  {formatNumber(receivablesSummary.paid_amount_chf)} CHF + Outstanding {formatNumber(receivablesSummary.outstanding_amount_chf)} CHF
                </span>
                {!receivablesSummary.reconciles ? (
                  <span className="ml-2 text-[11px]">Portfolio mismatch; check deal contract vs payment ledger.</span>
                ) : null}
              </div>

              <div className="mt-3 rounded-lg border border-gray-200 p-3" data-testid="receivables-monthly-breakdown">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Receivables by month (CHF)</p>
                  <InfoHint text="Open AR distributed by expected-paid date. Billed invoices (tranches) are bucketed in their own due month; overdue items stay anchored to their original month. Any remaining contract balance not yet on a tranche chip shows as a dashed amber 'uninvoiced' chip — its month follows Service Rendered Date when set on the deal (partial invoicing does not override that); if unset, it follows the deal portfolio due date. Refresh the Forecast tab after changing dates to reload buckets. Click a month (or the grey 'Outside selected period' rollup) to open the Deal Library filtered to the contributing deals; click the invoice summary on a row to expand the same per-invoice chip list as for months. Click any chip to open that deal." />
                </div>
                <div className="mt-3 space-y-2 md:hidden">
                  {receivablesMonthlyBreakdown.rows.length === 0 && !receivablesMonthlyBreakdown.outside ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                      No open receivables in selected period.
                    </div>
                  ) : (
                    <>
                      {receivablesMonthlyBreakdown.rows.map((r) => {
                        const isOpen = expandedMonths.has(r.month);
                        const hasInvoices = (r.invoices?.length ?? 0) > 0;
                        return (
                          <div
                            key={`ar-mobile-month-${r.month}`}
                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-left shadow-sm"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <button
                                type="button"
                                className="truncate text-left text-sm font-medium text-sky-700 underline-offset-2 hover:underline disabled:cursor-default disabled:text-gray-800 disabled:no-underline"
                                onClick={() => (r.deal_ids?.length ?? 0) > 0 && onOpenBucketDeals?.({ view: 'receivables_month', bucket: r.month, filters: receivablesQueryParams })}
                                disabled={(r.deal_ids?.length ?? 0) === 0}
                                title={(r.deal_ids?.length ?? 0) > 0
                                  ? `Open Deal Library filtered to ${r.deal_ids.length} deal${r.deal_ids.length === 1 ? '' : 's'} contributing to ${formatMonthLabel(r.month)}`
                                  : 'No deals contributing to this month'}
                              >
                                {formatMonthLabel(r.month)}
                              </button>
                              <span className={`font-mono text-xs tabular-nums ${negativePurpleClass(r.total_chf)}`}>
                                {formatNumber(r.total_chf)} CHF
                              </span>
                            </div>
                            {hasInvoices ? (
                              <button
                                type="button"
                                onClick={() => toggleMonthExpanded(r.month)}
                                className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
                              >
                                <span>{isOpen ? '▾' : '▸'}</span>
                                <span>{r.invoices.length} invoice{r.invoices.length === 1 ? '' : 's'}</span>
                              </button>
                            ) : (
                              <p className="mt-1 text-[11px] text-gray-400">No invoices</p>
                            )}
                            {isOpen && hasInvoices ? (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {r.invoices.map((inv, i) => (
                                  <MonthlyInvoiceChip key={`m-inv-${r.month}-${inv.tranche_id || `u-${i}`}`} invoice={inv} onOpenDeal={onOpenDeal} />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                      {receivablesMonthlyBreakdown.outside ? (() => {
                        const o = receivablesMonthlyBreakdown.outside;
                        const isOpen = expandedMonths.has(RECEIVABLES_OUTSIDE_EXPAND_KEY);
                        const hasInvoices = (o.invoices?.length ?? 0) > 0;
                        const hasDeals = (o.deal_ids?.length ?? 0) > 0;
                        return (
                          <div
                            key="ar-mobile-month-outside"
                            className="w-full rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2 text-left text-gray-600 shadow-sm"
                            title="Open receivables whose expected-paid month is before or after the forecast period shown above — same invoice chips as a month row when expanded."
                          >
                            <div className="flex items-center justify-between gap-2">
                              <button
                                type="button"
                                className="truncate text-left text-sm font-medium italic text-sky-700 underline-offset-2 hover:underline disabled:cursor-default disabled:text-gray-600 disabled:no-underline"
                                onClick={() => hasDeals && onOpenBucketDeals?.({
                                  view: 'receivables_month',
                                  bucket: 'outside_period',
                                  filters: receivablesOutsideDrillFilters,
                                })}
                                disabled={!hasDeals}
                                title={hasDeals
                                  ? `Open Deal Library filtered to ${o.deal_ids.length} deal${o.deal_ids.length === 1 ? '' : 's'} with receivables outside the selected period`
                                  : 'No deals in this rollup'}
                              >
                                Outside selected period
                              </button>
                              <span className={`font-mono text-xs tabular-nums ${negativePurpleClass(o.total_chf)}`}>
                                {formatNumber(o.total_chf)} CHF
                              </span>
                            </div>
                            {o.overdue_chf > 0 ? (
                              <p className="mt-0.5 text-[11px] text-red-500">
                                Overdue {formatNumber(o.overdue_chf)} CHF
                              </p>
                            ) : null}
                            {hasInvoices ? (
                              <button
                                type="button"
                                onClick={() => toggleMonthExpanded(RECEIVABLES_OUTSIDE_EXPAND_KEY)}
                                className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
                              >
                                <span>{isOpen ? '▾' : '▸'}</span>
                                <span>{o.invoices.length} invoice{o.invoices.length === 1 ? '' : 's'}</span>
                              </button>
                            ) : (
                              <p className="mt-1 text-[11px] text-gray-400">No invoices</p>
                            )}
                            {isOpen && hasInvoices ? (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {o.invoices.map((inv, i) => (
                                  <MonthlyInvoiceChip key={`m-inv-outside-${inv.tranche_id || `u-${i}`}`} invoice={inv} onOpenDeal={onOpenDeal} />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })() : null}
                    </>
                  )}
                </div>
                <div className="pl-table-shell mt-3 hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[720px] text-sm" data-testid="receivables-by-month-table">
                    <thead className="pl-table-header bg-white text-xs text-gray-500">
                      <tr>
                        <th className="pl-table-head-cell text-left">Month (expected paid date)</th>
                        <th className="pl-table-head-cell text-right">Open receivables</th>
                        <th className="pl-table-head-cell text-left">Billing detail (by due month)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivablesMonthlyBreakdown.rows.length === 0 && !receivablesMonthlyBreakdown.outside ? (
                        <tr><td className="px-3 py-4 text-sm text-gray-500" colSpan={3}>No open receivables in selected period.</td></tr>
                      ) : (
                        <>
                          {receivablesMonthlyBreakdown.rows.map((r) => {
                            const isOpen = expandedMonths.has(r.month);
                            const hasInvoices = (r.invoices?.length ?? 0) > 0;
                            const hasDeals = (r.deal_ids?.length ?? 0) > 0;
                            return (
                              <tr
                                key={`ar-month-${r.month}`}
                                className="pl-row border-t border-gray-100 align-top"
                                data-testid={`receivables-monthly-row-${r.month}`}
                              >
                                <td className="pl-cell-text">
                                  {hasDeals ? (
                                    <button
                                      type="button"
                                      className="rounded px-0 text-left text-sky-700 underline-offset-2 hover:underline"
                                      onClick={() => onOpenBucketDeals?.({ view: 'receivables_month', bucket: r.month, filters: receivablesQueryParams })}
                                      title={`Open Deal Library filtered to ${r.deal_ids.length} deal${r.deal_ids.length === 1 ? '' : 's'} contributing to ${formatMonthLabel(r.month)}`}
                                    >
                                      {formatMonthLabel(r.month)}
                                    </button>
                                  ) : (
                                    <span className="text-gray-500">{formatMonthLabel(r.month)}</span>
                                  )}
                                </td>
                                <td className={`pl-cell-num ${negativePurpleClass(r.total_chf)}`}>{formatNumber(r.total_chf)} CHF</td>
                                <td className="pl-cell-text">
                                  {!hasInvoices ? (
                                    <span className="text-xs text-gray-400">—</span>
                                  ) : !isOpen ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleMonthExpanded(r.month)}
                                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                      data-testid={`receivables-month-expand-${r.month}`}
                                    >
                                      <span>▸</span>
                                      <span>{r.invoices.length} invoice{r.invoices.length === 1 ? '' : 's'}</span>
                                      {(r.overdue_chf || 0) > 0 ? (
                                        <span className="ml-1 rounded-sm bg-red-100 px-1 py-0 text-[10px] font-semibold text-red-700">
                                          overdue
                                        </span>
                                      ) : null}
                                    </button>
                                  ) : (
                                    <div className="space-y-1">
                                      <button
                                        type="button"
                                        onClick={() => toggleMonthExpanded(r.month)}
                                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                      >
                                        <span>▾</span>
                                        <span>{r.invoices.length} invoice{r.invoices.length === 1 ? '' : 's'}</span>
                                      </button>
                                      <div className="flex flex-wrap gap-1">
                                        {r.invoices.map((inv, i) => (
                                          <MonthlyInvoiceChip
                                            key={`d-inv-${r.month}-${inv.tranche_id || `u-${i}`}`}
                                            invoice={inv}
                                            onOpenDeal={onOpenDeal}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {receivablesMonthlyBreakdown.outside ? (() => {
                            const o = receivablesMonthlyBreakdown.outside;
                            const isOpen = expandedMonths.has(RECEIVABLES_OUTSIDE_EXPAND_KEY);
                            const hasInvoices = (o.invoices?.length ?? 0) > 0;
                            const hasDeals = (o.deal_ids?.length ?? 0) > 0;
                            return (
                              <tr
                                key="ar-month-outside"
                                className="pl-row border-t border-gray-100 bg-gray-50/60 align-top text-gray-600"
                                data-testid="receivables-monthly-row-outside"
                                title="Open receivables whose expected-paid month is before or after the forecast period — expand for the same per-invoice detail as month rows."
                              >
                                <td className="pl-cell-text">
                                  {hasDeals ? (
                                    <button
                                      type="button"
                                      className="rounded px-0 text-left text-sm italic text-sky-700 underline-offset-2 hover:underline"
                                      onClick={() => onOpenBucketDeals?.({
                                        view: 'receivables_month',
                                        bucket: 'outside_period',
                                        filters: receivablesOutsideDrillFilters,
                                      })}
                                      title={`Open Deal Library filtered to ${o.deal_ids.length} deal${o.deal_ids.length === 1 ? '' : 's'} with receivables outside the selected period`}
                                    >
                                      Outside selected period
                                    </button>
                                  ) : (
                                    <span className="text-sm italic text-gray-500">Outside selected period</span>
                                  )}
                                </td>
                                <td className={`pl-cell-num ${negativePurpleClass(o.total_chf)}`}>{formatNumber(o.total_chf)} CHF</td>
                                <td className="pl-cell-text">
                                  {!hasInvoices ? (
                                    <span className="text-xs text-gray-400">—</span>
                                  ) : !isOpen ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleMonthExpanded(RECEIVABLES_OUTSIDE_EXPAND_KEY)}
                                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                      data-testid="receivables-month-expand-outside"
                                    >
                                      <span>▸</span>
                                      <span>{o.invoices.length} invoice{o.invoices.length === 1 ? '' : 's'}</span>
                                      {(o.overdue_chf || 0) > 0 ? (
                                        <span className="ml-1 rounded-sm bg-red-100 px-1 py-0 text-[10px] font-semibold text-red-700">
                                          overdue
                                        </span>
                                      ) : null}
                                    </button>
                                  ) : (
                                    <div className="space-y-1">
                                      <button
                                        type="button"
                                        onClick={() => toggleMonthExpanded(RECEIVABLES_OUTSIDE_EXPAND_KEY)}
                                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                      >
                                        <span>▾</span>
                                        <span>{o.invoices.length} invoice{o.invoices.length === 1 ? '' : 's'}</span>
                                      </button>
                                      <div className="flex flex-wrap gap-1">
                                        {o.invoices.map((inv, i) => (
                                          <MonthlyInvoiceChip
                                            key={`d-inv-outside-${inv.tranche_id || `u-${i}`}`}
                                            invoice={inv}
                                            onOpenDeal={onOpenDeal}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })() : null}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Input value={arClientFilter} onChange={(e) => setArClientFilter(e.target.value)} placeholder="Filter client" className="h-8" />
                <Input value={arDealFilter} onChange={(e) => setArDealFilter(e.target.value)} placeholder="Filter deal id" className="h-8" />
                <Input value={arEntityFilter} onChange={(e) => setArEntityFilter(e.target.value)} placeholder="Filter entity" className="h-8" />
                <Input
                  type="month"
                  value={arMonthFilter}
                  onChange={(e) => setArMonthFilter(e.target.value)}
                  className="h-8"
                  title="Restrict open AR to deals whose reference invoice date falls in the given YYYY-MM. Applies to all Receivables panels (summary KPIs, by month, projects table). NOTE: this is the reference invoice date on the deal, NOT the expected-paid date that drives the monthly buckets — so the 'Receivables by month' table will still distribute the filtered records across their respective due months. Leave empty for full open AR."
                  placeholder="Invoice-month filter (optional)"
                />
              </div>

              <div className="mt-3 flex items-center gap-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Projects accounted for in Total Receivables</p>
                <InfoHint text="One row per project (deal) with a non-zero open AR balance. The last column follows the active KPI card: Outstanding (total), Invoiced (open), Uninvoiced (open), or Overdue. Issued-invoice chips show open amounts; Overdue drill lists only past-due invoices. Deal value / Invoiced % / Invoiced stay as deal-context columns (cumulative billing vs contract)." />
                {arDrillMode === 'overdue' ? (
                  <span className="rounded-sm bg-red-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-red-700" data-testid="receivables-projects-drill-badge">
                    Filtered: overdue only
                  </span>
                ) : null}
                {arDrillMode === 'invoiced' ? (
                  <span className="rounded-sm bg-sky-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-sky-700" data-testid="receivables-projects-drill-badge">
                    Filtered: invoiced open
                  </span>
                ) : null}
                {arDrillMode === 'uninvoiced' ? (
                  <span className="rounded-sm bg-amber-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-amber-800" data-testid="receivables-projects-drill-badge">
                    Filtered: uninvoiced open
                  </span>
                ) : null}
              </div>

              <div className="mt-3 space-y-2 md:hidden">
                {displayedProjectRows.length === 0 ? (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                    {arDrillEmptyMessage}
                  </div>
                ) : displayedProjectRows.map((row) => {
                  const kpiAmt = projectKpiAmount(row);
                  const issued = projectIssuedForDrill(row);
                  const showPaid = arDrillMode === 'all' || arDrillMode === 'invoiced';
                  return (
                  <button
                    key={`ar-mobile-proj-${row.deal_id}`}
                    type="button"
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-3 text-left shadow-sm active:bg-gray-50"
                    onClick={() => onOpenDeal?.(row.deal_id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-800">{row.client_name || 'Unknown client'}</p>
                        <p className="truncate text-xs text-gray-600">{row.project_name || 'Untitled'}</p>
                      </div>
                      <span className={`text-xs tabular-nums ${kpiAmt > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                        {formatNumber(kpiAmt)} CHF
                      </span>
                    </div>
                    <p className="mt-0.5 text-right text-[10px] uppercase tracking-wider text-gray-400">{arKpiAmountLabel}</p>
                    <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt className="text-gray-400">Deal value</dt>
                        <dd className="font-mono text-gray-800">{formatNumber(row.deal_value_chf)} CHF</dd>
                      </div>
                      <div>
                        <dt className="text-gray-400">Invoiced</dt>
                        <dd className="font-mono text-gray-800">
                          {formatNumber(row.invoiced_chf)} CHF
                          <span className="ml-1 text-[10px] text-gray-500">({formatPercent(row.invoiced_percentage)})</span>
                        </dd>
                      </div>
                    </dl>
                    {arDrillMode === 'uninvoiced' && kpiAmt > 0.005 ? (
                      <div className="mt-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Uninvoiced</p>
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-dashed border-amber-300 bg-amber-50 px-1 py-0 text-[10px] font-mono leading-4 text-amber-900">
                            (uninvoiced)
                            <span className="text-[10px] font-normal text-gray-500">{formatNumber(kpiAmt)} CHF</span>
                          </span>
                        </div>
                      </div>
                    ) : null}
                    {issued.length > 0 ? (
                      <div className="mt-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          {arDrillMode === 'overdue' ? 'Overdue invoices' : 'Issued'}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {issued.map((inv) => (
                            <InvoiceChip key={`m-iss-${inv.tranche_id}`} invoice={inv} tone="issued" />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {showPaid && (row.paid_invoices?.length ?? 0) > 0 ? (
                      <div className="mt-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Paid</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {row.paid_invoices.map((inv) => (
                            <InvoiceChip key={`m-pd-${inv.tranche_id}`} invoice={inv} tone="paid" />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </button>
                  );
                })}
              </div>
              {/*
                Compact table: no hard min-width so it flexes to the panel.
                A low `min-w-[960px]` is retained as a safety net for very
                narrow workspaces — above that it fits edge-to-edge without
                horizontal scrolling. Numeric cells use `whitespace-nowrap`
                so "52'736 CHF" stays on one line; client/project cells
                truncate with a `title` tooltip for the full value.
              */}
              <div className="pl-table-shell mt-3 hidden overflow-x-auto md:block">
                <table className="w-full min-w-[960px] table-fixed text-xs" data-testid="receivables-by-project-table">
                  <colgroup>
                    <col className="w-[13%]" />
                    <col className="w-[19%]" />
                    <col className="w-[20%]" />
                    <col className="w-[14%]" />
                    <col className="w-[9%]" />
                    <col className="w-[6%]" />
                    <col className="w-[9%]" />
                    <col className="w-[10%]" />
                  </colgroup>
                  <thead className="pl-table-header bg-white text-[11px] text-gray-500">
                    <tr>
                      <th className="pl-table-head-cell text-left">Client</th>
                      <th className="pl-table-head-cell text-left">Project</th>
                      <th className="pl-table-head-cell text-left">
                        {arDrillMode === 'overdue' ? 'Overdue invoices' : arDrillMode === 'uninvoiced' ? 'Billing detail' : 'Issued invoices'}
                      </th>
                      <th className="pl-table-head-cell text-left">Paid invoices</th>
                      <th className="pl-table-head-cell whitespace-nowrap text-right">Deal value</th>
                      <th className="pl-table-head-cell whitespace-nowrap text-right">Invoiced %</th>
                      <th className="pl-table-head-cell whitespace-nowrap text-right">Invoiced</th>
                      <th className="pl-table-head-cell whitespace-nowrap text-right">{arKpiAmountLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedProjectRows.length === 0 ? (
                      <tr><td className="px-3 py-4 text-xs text-gray-500" colSpan={8}>
                        {arDrillEmptyMessage}
                      </td></tr>
                    ) : displayedProjectRows.map((row) => {
                      const kpiAmt = projectKpiAmount(row);
                      const issued = projectIssuedForDrill(row);
                      const showPaid = arDrillMode === 'all' || arDrillMode === 'invoiced';
                      return (
                      <tr
                        key={`ar-proj-${row.deal_id}`}
                        className="pl-row cursor-pointer border-t border-gray-100 align-top hover:bg-sky-50/40"
                        onClick={() => onOpenDeal?.(row.deal_id)}
                        data-testid={`receivables-project-row-${row.deal_id}`}
                      >
                        <td
                          className="pl-cell-text align-top text-[11px]"
                          title={row.client_name || 'Unknown'}
                        >
                          <span className="block truncate font-medium text-gray-800">
                            {row.client_name || 'Unknown'}
                            {row.entity ? <span className="ml-1 text-[10px] text-gray-400">· {row.entity}</span> : null}
                          </span>
                        </td>
                        <td
                          className="pl-cell-text align-top text-[11px]"
                          title={row.project_name || 'Untitled'}
                        >
                          <button
                            type="button"
                            className="block w-full truncate rounded px-0 text-left text-sky-700 underline-offset-2 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenDeal?.(row.deal_id);
                            }}
                          >
                            {row.project_name || 'Untitled'}
                          </button>
                        </td>
                        <td className="pl-cell-text align-top text-[11px]">
                          {arDrillMode === 'uninvoiced' && kpiAmt > 0.005 ? (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-dashed border-amber-300 bg-amber-50 px-1 py-0 text-[10px] font-mono leading-4 text-amber-900">
                              (uninvoiced)
                              <span className="text-[10px] font-normal text-gray-500">{formatNumber(kpiAmt)} CHF</span>
                            </span>
                          ) : issued.length === 0 ? (
                            <span className="text-[11px] text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {issued.map((inv) => (
                                <InvoiceChip key={`iss-${inv.tranche_id}`} invoice={inv} tone="issued" />
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="pl-cell-text align-top text-[11px]">
                          {!showPaid || (row.paid_invoices?.length ?? 0) === 0 ? (
                            <span className="text-[11px] text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {row.paid_invoices.map((inv) => (
                                <InvoiceChip key={`pd-${inv.tranche_id}`} invoice={inv} tone="paid" />
                              ))}
                            </div>
                          )}
                        </td>
                        <td className={`pl-cell-num whitespace-nowrap align-top text-[11px] ${negativePurpleClass(row.deal_value_chf)}`}>{formatNumber(row.deal_value_chf)} CHF</td>
                        <td className="pl-cell-num whitespace-nowrap align-top text-[11px] text-gray-700">{formatPercent(row.invoiced_percentage)}</td>
                        <td className={`pl-cell-num whitespace-nowrap align-top text-[11px] ${negativePurpleClass(row.invoiced_chf)}`}>{formatNumber(row.invoiced_chf)} CHF</td>
                        <td className={`pl-cell-num whitespace-nowrap align-top text-[11px] font-semibold ${negativePurpleClass(kpiAmt)}`}>{formatNumber(kpiAmt)} CHF</td>
                      </tr>
                      );
                    })}
                  </tbody>
                  {displayedProjectTotals && displayedProjectRows.length > 0 ? (
                    <tfoot>
                      <tr
                        className="border-t-2 border-gray-300 bg-gray-50/80 text-[11px] font-semibold text-gray-800"
                        data-testid="receivables-by-project-totals"
                      >
                        <td className="pl-cell-text text-[11px]" colSpan={2}>Total ({displayedProjectRows.length} project{displayedProjectRows.length === 1 ? '' : 's'})</td>
                        <td className="pl-cell-text text-[11px] text-gray-500">—</td>
                        <td className="pl-cell-text text-[11px] text-gray-500">—</td>
                        <td className="pl-cell-num whitespace-nowrap text-[11px]">{formatNumber(displayedProjectTotals.deal_value_chf)} CHF</td>
                        <td className="pl-cell-num whitespace-nowrap text-[11px] text-gray-700">{formatPercent(displayedProjectTotals.invoiced_percentage)}</td>
                        <td className="pl-cell-num whitespace-nowrap text-[11px]">{formatNumber(displayedProjectTotals.invoiced_chf)} CHF</td>
                        <td className="pl-cell-num whitespace-nowrap text-[11px]" data-testid="receivables-by-project-kpi-total">{formatNumber(displayedProjectTotals.outstanding_chf)} CHF</td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
                {arDrillKpiMatchChf != null && displayedProjectTotals && displayedProjectRows.length > 0 ? (
                  <p className="mt-1.5 text-[10px] text-gray-500" data-testid="receivables-drill-kpi-match">
                    Portfolio deals {formatNumber(displayedProjectTotals.portfolio_kpi_chf)} CHF
                    {Math.abs((displayedProjectTotals.portfolio_kpi_chf || 0) - (arDrillKpiMatchChf || 0)) <= 0.05
                      ? ` matches ${arKpiAmountLabel} KPI`
                      : ` · KPI card ${formatNumber(arDrillKpiMatchChf)} CHF`}
                    {(displayedProjectTotals.maintenance_kpi_chf || 0) > 0.005
                      ? ` · Maintenance ${formatNumber(displayedProjectTotals.maintenance_kpi_chf)} CHF (shown below, outside deal-portfolio KPI)`
                      : ''}
                  </p>
                ) : null}
              </div>

              <div className="mt-3 rounded-md border border-gray-200 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  By client{arDrillMode !== 'all' ? ` · ${arKpiAmountLabel}` : ''}
                </p>
                <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                  {displayedClientRows.length === 0 ? (
                    <li className="text-xs text-gray-500">{arDrillEmptyMessage}</li>
                  ) : displayedClientRows.map((r) => (
                    <li key={r.client_name} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-gray-700">{r.client_name || 'Unknown'}</span>
                      <span className={`tabular-nums text-gray-600 ${negativePurpleClass(r.total_chf)}`}>{formatNumber(r.total_chf)} CHF</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      <BexioReconDrawer
        open={bexioReconOpen}
        onClose={() => setBexioReconOpen(false)}
        recon={bexioRecon}
        loading={bexioReconLoading}
        error={bexioReconError}
        onRefresh={loadBexioRecon}
        onCleanupOrphans={cleanupOrphans}
      />

      <ForecastKpiExplainSheet
        open={kpiExplainOpen}
        onOpenChange={(v) => {
          setKpiExplainOpen(v);
          if (!v) {
            setKpiExplainData(null);
            setKpiExplainMetric(null);
          }
        }}
        titleOverride={kpiExplainTitle}
        data={kpiExplainData}
        loading={kpiExplainLoading}
        onOpenDeal={onOpenDeal}
        cardTotalChf={
          kpiExplainData?.metric === 'committed_revenue'
            ? data?.committed_revenue_chf
            : kpiExplainData?.metric === 'weighted_revenue'
              ? data?.weighted_revenue_chf
              : kpiExplainData?.metric === 'forecast_margin'
                ? data?.forecast_margin_chf
                : undefined
        }
      />
    </div>
  );
}
